import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Listbox, ListboxButton, ListboxOption, ListboxOptions, Transition, Input, Dialog, DialogPanel, DialogTitle, TransitionChild } from "@headlessui/react";
import { ChevronDownIcon, CheckIcon, MagnifyingGlassIcon, XMarkIcon, ClipboardIcon } from "@heroicons/react/24/outline";
import { Button, Alert } from "@libs/rubick";
import { NodeListItem } from "../components/NodeListItem";
import { NodeOverview } from "../components/NodeOverview";
import { NodeHealthStatus } from "../types";
import { useApi } from "../../../app/apiContext";
import { useAppSelector } from "../../../app/hooks";
import { setNodeFilters, selectFilteredNodes, selectNodeStats } from "../../../app/slices/nodesSlice";
import { useDispatch } from "react-redux";
import { useState } from "react";
const statusOptions = [
    {
        value: "all",
        label: "All Statuses"
    },
    {
        value: NodeHealthStatus.Healthy,
        label: "Healthy"
    },
    {
        value: NodeHealthStatus.Error,
        label: "Error"
    },
    {
        value: NodeHealthStatus.Offline,
        label: "Offline"
    }
];
export function ClusterStatusView() {
    const { nodesApi, clustersApi } = useApi();
    const dispatch = useDispatch();
    const [showJoinTokenModal, setShowJoinTokenModal] = useState(false);
    const [joinTokenData, setJoinTokenData] = useState(null);
    const [tokenError, setTokenError] = useState(null);
    const [copiedToken, setCopiedToken] = useState(false);
    const { data: nodes = [], isLoading } = nodesApi.useGetNodesQuery();
    const [generateJoinToken, { isLoading: isGenerating }] = clustersApi.useGenerateJoinTokenMutation();
    const { search: searchTerm, status: statusFilter } = useAppSelector((state)=>state.nodes.filters);
    const stats = useAppSelector((state)=>selectNodeStats(state, nodes));
    const filteredNodes = useAppSelector((state)=>selectFilteredNodes(state, nodes));
    const selectedOption = statusOptions.find((opt)=>opt.value === statusFilter);
    const handleSearchChange = (value)=>{
        dispatch(setNodeFilters({
            search: value
        }));
    };
    const handleStatusChange = (value)=>{
        dispatch(setNodeFilters({
            status: value
        })); // The slice expects its own enum or string, this should work if compatible
    };
    const handleGenerateJoinToken = async ()=>{
        setTokenError(null);
        try {
            const result = await generateJoinToken().unwrap();
            setJoinTokenData(result);
            setShowJoinTokenModal(true);
        } catch (error) {
            const errorMessage = error?.data?.message || error?.message || "Failed to generate join token. Please try again.";
            setTokenError(errorMessage);
        }
    };
    const handleCloseJoinTokenModal = ()=>{
        setShowJoinTokenModal(false);
        setJoinTokenData(null);
        setCopiedToken(false);
    };
    const handleCopyToken = ()=>{
        if (joinTokenData?.token) {
            navigator.clipboard.writeText(joinTokenData.token);
            setCopiedToken(true);
            setTimeout(()=>setCopiedToken(false), 2000);
        }
    };
    if (isLoading) {
        return /*#__PURE__*/ _jsx("div", {
            className: "p-8",
            children: "Loading cluster status..."
        });
    }
    return /*#__PURE__*/ _jsx("div", {
        className: "min-h-screen bg-white",
        children: /*#__PURE__*/ _jsxs("div", {
            className: "max-w-7xl mx-auto p-8",
            children: [
                /*#__PURE__*/ _jsxs("div", {
                    className: "flex items-center justify-between mb-8",
                    children: [
                        /*#__PURE__*/ _jsxs("div", {
                            children: [
                                /*#__PURE__*/ _jsx("h1", {
                                    className: "text-2xl font-semibold text-gray-900",
                                    children: "Cluster Status"
                                }),
                                /*#__PURE__*/ _jsxs("p", {
                                    className: "text-sm text-gray-600 mt-1",
                                    children: [
                                        stats.totalNodes,
                                        " total nodes • ",
                                        stats.healthy,
                                        " healthy •",
                                        " ",
                                        stats.error,
                                        " error • ",
                                        stats.offline,
                                        " offline"
                                    ]
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsx(Button, {
                            type: "button",
                            variant: "simple",
                            color: "navy-90",
                            onClick: handleGenerateJoinToken,
                            disabled: isGenerating,
                            children: isGenerating ? "Generating..." : "Generate Join Token"
                        })
                    ]
                }),
                tokenError && /*#__PURE__*/ _jsx("div", {
                    className: "mb-6",
                    children: /*#__PURE__*/ _jsx(Alert, {
                        title: "Error",
                        children: tokenError
                    })
                }),
                /*#__PURE__*/ _jsxs("div", {
                    className: "bg-gray-50 border border-gray-200 rounded-lg p-6 mb-6",
                    children: [
                        /*#__PURE__*/ _jsxs("div", {
                            className: "flex justify-between items-center mb-4",
                            children: [
                                /*#__PURE__*/ _jsx("h2", {
                                    className: "text-lg font-semibold text-gray-900",
                                    children: "Node Overview"
                                }),
                                /*#__PURE__*/ _jsxs("div", {
                                    className: "flex items-center gap-4 text-xs text-gray-600",
                                    children: [
                                        /*#__PURE__*/ _jsxs("div", {
                                            className: "flex items-center gap-1.5",
                                            children: [
                                                /*#__PURE__*/ _jsx("div", {
                                                    className: "w-3 h-3 bg-green-500 rounded-sm"
                                                }),
                                                /*#__PURE__*/ _jsxs("span", {
                                                    children: [
                                                        "Healthy (",
                                                        stats.healthy,
                                                        ")"
                                                    ]
                                                })
                                            ]
                                        }),
                                        /*#__PURE__*/ _jsxs("div", {
                                            className: "flex items-center gap-1.5",
                                            children: [
                                                /*#__PURE__*/ _jsx("div", {
                                                    className: "w-3 h-3 bg-red-500 rounded-sm"
                                                }),
                                                /*#__PURE__*/ _jsxs("span", {
                                                    children: [
                                                        "Error (",
                                                        stats.error,
                                                        ")"
                                                    ]
                                                })
                                            ]
                                        }),
                                        /*#__PURE__*/ _jsxs("div", {
                                            className: "flex items-center gap-1.5",
                                            children: [
                                                /*#__PURE__*/ _jsx("div", {
                                                    className: "w-3 h-3 bg-gray-400 rounded-sm"
                                                }),
                                                /*#__PURE__*/ _jsxs("span", {
                                                    children: [
                                                        "Offline (",
                                                        stats.offline,
                                                        ")"
                                                    ]
                                                })
                                            ]
                                        })
                                    ]
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsx(NodeOverview, {
                            nodes: nodes
                        })
                    ]
                }),
                /*#__PURE__*/ _jsxs("div", {
                    className: "flex items-center gap-4 mb-6",
                    children: [
                        /*#__PURE__*/ _jsx("div", {
                            className: "flex-1 relative",
                            children: /*#__PURE__*/ _jsxs("div", {
                                className: "flex h-10 items-center rounded-lg border border-gray-300 bg-white pl-3 text-sm focus-within:ring-2 focus-within:ring-indigo-500",
                                children: [
                                    /*#__PURE__*/ _jsx(MagnifyingGlassIcon, {
                                        className: "w-4 h-4"
                                    }),
                                    /*#__PURE__*/ _jsx(Input, {
                                        type: "search",
                                        placeholder: "Search nodes by ID, description, or location...",
                                        className: "w-full pl-6 pr-4 py-2 placeholder:text-gray-400 focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50 bg-transparent",
                                        value: searchTerm,
                                        onChange: (e)=>handleSearchChange(e.target.value)
                                    })
                                ]
                            })
                        }),
                        /*#__PURE__*/ _jsx(Listbox, {
                            value: statusFilter,
                            onChange: handleStatusChange,
                            children: /*#__PURE__*/ _jsxs("div", {
                                className: "relative",
                                children: [
                                    /*#__PURE__*/ _jsxs(ListboxButton, {
                                        className: "relative cursor-default rounded-lg bg-white py-2 pl-4 pr-10 text-left border border-gray-300 min-w-[160px] focus:outline-none focus:ring-2 focus:ring-indigo-500",
                                        children: [
                                            /*#__PURE__*/ _jsx("span", {
                                                className: "block truncate text-sm",
                                                children: selectedOption?.label
                                            }),
                                            /*#__PURE__*/ _jsx("span", {
                                                className: "pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3",
                                                children: /*#__PURE__*/ _jsx(ChevronDownIcon, {
                                                    className: "h-4 w-4 text-gray-400",
                                                    "aria-hidden": "true"
                                                })
                                            })
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsx(Transition, {
                                        enter: "transition duration-100 ease-out",
                                        enterFrom: "opacity-0",
                                        enterTo: "opacity-100",
                                        leave: "transition ease-in duration-75",
                                        leaveFrom: "opacity-100",
                                        leaveTo: "opacity-0",
                                        children: /*#__PURE__*/ _jsx(ListboxOptions, {
                                            className: "absolute right-0 mt-1 max-h-60 overflow-auto rounded-lg bg-white py-1 text-sm shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none z-10 min-w-[160px]",
                                            children: statusOptions.map((option)=>/*#__PURE__*/ _jsx(ListboxOption, {
                                                    className: ({ active })=>`relative cursor-default select-none py-2 pl-10 pr-4 ${active ? "bg-indigo-50 text-indigo-900" : "text-gray-900"}`,
                                                    value: option.value,
                                                    children: ({ selected })=>/*#__PURE__*/ _jsxs(_Fragment, {
                                                            children: [
                                                                /*#__PURE__*/ _jsx("span", {
                                                                    className: `block truncate ${selected ? "font-medium" : "font-normal"}`,
                                                                    children: option.label
                                                                }),
                                                                selected && /*#__PURE__*/ _jsx("span", {
                                                                    className: "absolute inset-y-0 left-0 flex items-center pl-3 text-indigo-600",
                                                                    children: /*#__PURE__*/ _jsx(CheckIcon, {
                                                                        className: "h-4 w-4",
                                                                        "aria-hidden": "true"
                                                                    })
                                                                })
                                                            ]
                                                        })
                                                }, option.value))
                                        })
                                    })
                                ]
                            })
                        })
                    ]
                }),
                /*#__PURE__*/ _jsx("div", {
                    className: "space-y-4",
                    children: filteredNodes.length === 0 ? /*#__PURE__*/ _jsx("div", {
                        className: "bg-gray-50 rounded-lg border border-gray-200 p-12 text-center",
                        children: /*#__PURE__*/ _jsx("p", {
                            className: "text-gray-500",
                            children: "No nodes found matching your search criteria."
                        })
                    }) : filteredNodes.map((node)=>/*#__PURE__*/ _jsx(NodeListItem, {
                            node: node
                        }, node.nodeId))
                }),
                /*#__PURE__*/ _jsx(Transition, {
                    appear: true,
                    show: showJoinTokenModal,
                    children: /*#__PURE__*/ _jsxs(Dialog, {
                        className: "relative z-50",
                        onClose: handleCloseJoinTokenModal,
                        children: [
                            /*#__PURE__*/ _jsx(TransitionChild, {
                                enter: "ease-out duration-300",
                                enterFrom: "opacity-0",
                                enterTo: "opacity-100",
                                leave: "ease-in duration-200",
                                leaveFrom: "opacity-100",
                                leaveTo: "opacity-0",
                                children: /*#__PURE__*/ _jsx("div", {
                                    className: "fixed inset-0 bg-black bg-opacity-50"
                                })
                            }),
                            /*#__PURE__*/ _jsx("div", {
                                className: "fixed inset-0 overflow-y-auto",
                                children: /*#__PURE__*/ _jsx("div", {
                                    className: "flex min-h-full items-center justify-center p-4 text-center",
                                    children: /*#__PURE__*/ _jsx(TransitionChild, {
                                        enter: "ease-out duration-300",
                                        enterFrom: "opacity-0 scale-95",
                                        enterTo: "opacity-100 scale-100",
                                        leave: "ease-in duration-200",
                                        leaveFrom: "opacity-100 scale-100",
                                        leaveTo: "opacity-0 scale-95",
                                        children: /*#__PURE__*/ _jsxs(DialogPanel, {
                                            className: "w-full max-w-2xl transform overflow-visible rounded-xl bg-white p-6 text-left align-middle shadow-xl transition-all",
                                            children: [
                                                /*#__PURE__*/ _jsxs("div", {
                                                    className: "flex items-center justify-between mb-6",
                                                    children: [
                                                        /*#__PURE__*/ _jsx(DialogTitle, {
                                                            as: "h3",
                                                            className: "text-xl font-semibold text-gray-900",
                                                            children: "Join Token Generated"
                                                        }),
                                                        /*#__PURE__*/ _jsx("button", {
                                                            onClick: handleCloseJoinTokenModal,
                                                            className: "p-2 text-gray-400 hover:text-gray-600 transition-colors rounded-lg hover:bg-gray-100",
                                                            children: /*#__PURE__*/ _jsx(XMarkIcon, {
                                                                className: "h-5 w-5"
                                                            })
                                                        })
                                                    ]
                                                }),
                                                joinTokenData && /*#__PURE__*/ _jsxs("div", {
                                                    className: "space-y-4",
                                                    children: [
                                                        /*#__PURE__*/ _jsxs("div", {
                                                            children: [
                                                                /*#__PURE__*/ _jsx("label", {
                                                                    className: "block text-sm font-medium text-gray-700 mb-2",
                                                                    children: "Join Token"
                                                                }),
                                                                /*#__PURE__*/ _jsxs("div", {
                                                                    className: "relative",
                                                                    children: [
                                                                        /*#__PURE__*/ _jsx("div", {
                                                                            className: "bg-gray-50 rounded-lg p-3 pr-10 border border-gray-200",
                                                                            children: /*#__PURE__*/ _jsx("code", {
                                                                                className: "text-xs break-all text-gray-800",
                                                                                children: joinTokenData.token
                                                                            })
                                                                        }),
                                                                        /*#__PURE__*/ _jsx("button", {
                                                                            onClick: handleCopyToken,
                                                                            className: "absolute right-2 top-2 p-1 text-gray-500 hover:text-gray-700",
                                                                            children: /*#__PURE__*/ _jsx(ClipboardIcon, {
                                                                                className: `h-5 w-5 transition-all duration-200 ${copiedToken ? "text-green-500" : ""}`
                                                                            })
                                                                        })
                                                                    ]
                                                                })
                                                            ]
                                                        }),
                                                        /*#__PURE__*/ _jsx("div", {
                                                            className: "bg-yellow-50 border border-yellow-200 rounded-lg p-4",
                                                            children: /*#__PURE__*/ _jsxs("p", {
                                                                className: "text-sm text-yellow-800",
                                                                children: [
                                                                    /*#__PURE__*/ _jsx("strong", {
                                                                        children: "Note:"
                                                                    }),
                                                                    " This token is valid for 10 minutes. Share it securely with the node you want to add to the cluster."
                                                                ]
                                                            })
                                                        }),
                                                        /*#__PURE__*/ _jsx("div", {
                                                            className: "flex justify-end",
                                                            children: /*#__PURE__*/ _jsx(Button, {
                                                                type: "button",
                                                                variant: "simple",
                                                                color: "navy-90",
                                                                onClick: handleCloseJoinTokenModal,
                                                                children: "Close"
                                                            })
                                                        })
                                                    ]
                                                })
                                            ]
                                        })
                                    })
                                })
                            })
                        ]
                    })
                })
            ]
        })
    });
}
